import type { Stream, RequestOptions } from "@dxos/codec-protobuf";
import substitutions from "../../../substitutions.js";
import * as dxos_agent_dashboard from "../agent/dashboard.js";
import * as dxos_agent_echoproxy from "../agent/echoproxy.js";
import * as dxos_agent_epoch from "../agent/epoch.js";
import * as dxos_agent_functions from "../agent/functions.js";
import * as dxos_bot from "../bot.js";
import * as dxos_client_services from "../client/services.js";
import * as dxos_config from "../config.js";
import * as dxos_devtools_host from "../devtools/host.js";
import * as dxos_devtools_swarm from "../devtools/swarm.js";
import * as dxos_echo_blob from "./blob.js";
import * as dxos_echo_feed from "./feed.js";
import * as dxos_echo_filter from "./filter.js";
import * as dxos_echo_indexing from "./indexing.js";
import * as dxos_echo_model_document from "./model/document.js";
import * as dxos_echo_model_messenger from "./model/messenger.js";
import * as dxos_echo_model_text from "./model/text.js";
import * as dxos_echo_object from "./object.js";
import * as dxos_echo_query from "./query.js";
import * as dxos_echo_service from "./service.js";
import * as dxos_echo_snapshot from "./snapshot.js";
import * as dxos_echo_timeframe from "./timeframe.js";
import * as dxos_edge_calls from "../edge/calls.js";
import * as dxos_edge_messenger from "../edge/messenger.js";
import * as dxos_edge_signal from "../edge/signal.js";
import * as dxos_error from "../error.js";
import * as dxos_gravity from "../gravity.js";
import * as dxos_halo_credentials from "../halo/credentials.js";
import * as dxos_halo_credentials_auth from "../halo/credentials/auth.js";
import * as dxos_halo_credentials_greet from "../halo/credentials/greet.js";
import * as dxos_halo_credentials_identity from "../halo/credentials/identity.js";
import * as dxos_halo_invitations from "../halo/invitations.js";
import * as dxos_halo_keyring from "../halo/keyring.js";
import * as dxos_halo_keys from "../halo/keys.js";
import * as dxos_halo_signed from "../halo/signed.js";
import * as dxos_iframe from "../iframe.js";
import * as dxos_keys from "../keys.js";
import * as dxos_mesh_bridge from "../mesh/bridge.js";
import * as dxos_mesh_broadcast from "../mesh/broadcast.js";
import * as dxos_mesh_messaging from "../mesh/messaging.js";
import * as dxos_mesh_muxer from "../mesh/muxer.js";
import * as dxos_mesh_presence from "../mesh/presence.js";
import * as dxos_mesh_protocol from "../mesh/protocol.js";
import * as dxos_mesh_replicator from "../mesh/replicator.js";
import * as dxos_mesh_signal from "../mesh/signal.js";
import * as dxos_mesh_swarm from "../mesh/swarm.js";
import * as dxos_mesh_teleport from "../mesh/teleport.js";
import * as dxos_mesh_teleport_auth from "../mesh/teleport/auth.js";
import * as dxos_mesh_teleport_automerge from "../mesh/teleport/automerge.js";
import * as dxos_mesh_teleport_blobsync from "../mesh/teleport/blobsync.js";
import * as dxos_mesh_teleport_control from "../mesh/teleport/control.js";
import * as dxos_mesh_teleport_gossip from "../mesh/teleport/gossip.js";
import * as dxos_mesh_teleport_notarization from "../mesh/teleport/notarization.js";
import * as dxos_mesh_teleport_replicator from "../mesh/teleport/replicator.js";
import * as dxos_registry from "../registry.js";
import * as dxos_rpc from "../rpc.js";
import * as dxos_service_agentmanager from "../service/agentmanager.js";
import * as dxos_service_publisher from "../service/publisher.js";
import * as dxos_service_supervisor from "../service/supervisor.js";
import * as dxos_service_tunnel from "../service/tunnel.js";
import * as dxos_tracing from "../tracing.js";
import * as dxos_type from "../type.js";
import * as dxos_value from "../value.js";
import * as example_testing_data from "../../example/testing/data.js";
import * as example_testing_rpc from "../../example/testing/rpc.js";
import * as google_protobuf from "../../google/protobuf.js";
/**
 * Main metadata record.
 *
 * Defined in: `../../../dxos/echo/metadata.proto`
 */
export interface EchoMetadata {
    /**
     * Version number that is incremented every time there's a breaking change to the storage schema.
     * This is used to detect if the storage schema has changed since the last time the client was run.
     * Loading data from a different version will might result in an error.
     */
    version: number;
    created: ReturnType<(typeof substitutions)["google.protobuf.Timestamp"]["decode"]>;
    updated: ReturnType<(typeof substitutions)["google.protobuf.Timestamp"]["decode"]>;
    /**
     * Options:
     *   - proto3_optional = true
     */
    identity?: IdentityRecord;
    /**
     * List of the data spaces.
     */
    spaces?: SpaceMetadata[];
    invitations?: dxos_client_services.Invitation[];
    /**
     * Keys of spaces that have been tombstoned (soft-deleted). Survives restart; prevents re-acceptance.
     */
    deletedSpaces?: ReturnType<(typeof substitutions)["dxos.keys.PublicKey"]["decode"]>[];
}
/**
 * Per-space metadata. Frequent updates. Keep small.
 *
 * Defined in: `../../../dxos/echo/metadata.proto`
 */
export interface SmallSpaceMetadata {
}
/**
 * Per-space metadata. Infrequent updates.
 *
 * Defined in: `../../../dxos/echo/metadata.proto`
 */
export interface LargeSpaceMetadata {
    /**
     * Options:
     *   - proto3_optional = true
     */
    controlPipelineSnapshot?: ControlPipelineSnapshot;
}
/**
 * Defined in: `../../../dxos/echo/metadata.proto`
 */
export interface SpaceCache {
    /**
     * Properties snapshot.
     *
     * Options:
     *   - proto3_optional = true
     */
    properties?: dxos_echo_model_document.ObjectSnapshot;
}
/**
 * Defined in: `../../../dxos/echo/metadata.proto`
 */
export interface SpaceMetadata {
    /**
     * Space key.
     */
    key: ReturnType<(typeof substitutions)["dxos.keys.PublicKey"]["decode"]>;
    /**
     * Immutable tags assigned at space creation time.
     */
    tags?: string[];
    /**
     * ACTIVE, INACTIVE, etc.
     *
     * Options:
     *   - proto3_optional = true
     */
    state?: dxos_client_services.SpaceState;
    /**
     * Options:
     *   - proto3_optional = true
     */
    genesisFeedKey?: ReturnType<(typeof substitutions)["dxos.keys.PublicKey"]["decode"]>;
    /**
     * Key of the writable control feed.
     *
     * Options:
     *   - proto3_optional = true
     */
    controlFeedKey?: ReturnType<(typeof substitutions)["dxos.keys.PublicKey"]["decode"]>;
    /**
     * List of all feed associated with the space.
     */
    feedKeys?: ReturnType<(typeof substitutions)["dxos.keys.PublicKey"]["decode"]>[];
    /**
     * Key of the writable data feed.
     *
     * Options:
     *   - proto3_optional = true
     */
    dataFeedKey?: ReturnType<(typeof substitutions)["dxos.keys.PublicKey"]["decode"]>;
    /**
     * Options:
     *   - proto3_optional = true
     */
    controlTimeframe?: ReturnType<(typeof substitutions)["dxos.echo.timeframe.TimeframeVector"]["decode"]>;
    /**
     * Options:
     *   - proto3_optional = true
     */
    dataTimeframe?: ReturnType<(typeof substitutions)["dxos.echo.timeframe.TimeframeVector"]["decode"]>;
    /**
     * Options:
     *   - proto3_optional = true
     */
    cache?: SpaceCache;
    /**
     * Options:
     *   - proto3_optional = true
     */
    edgeReplication?: EdgeReplicationSetting;
}
/**
 * Defined in:
 *   {@link file://./../../../dxos/echo/metadata.proto}
 */
export enum EdgeReplicationSetting {
    DISABLED = 0,
    ENABLED = 1
}
/**
 * Defined in: `../../../dxos/echo/metadata.proto`
 */
export interface ControlPipelineSnapshot {
    /**
     * Timeframe of the last message included in the snapshot.
     */
    timeframe: ReturnType<(typeof substitutions)["dxos.echo.timeframe.TimeframeVector"]["decode"]>;
    messages?: ControlPipelineSnapshot.ControlMessage[];
}
export namespace ControlPipelineSnapshot {
    /**
     * Defined in: `../../../dxos/echo/metadata.proto`
     */
    export interface ControlMessage {
        feedKey: ReturnType<(typeof substitutions)["dxos.keys.PublicKey"]["decode"]>;
        credential: dxos_halo_credentials.Credential;
    }
}
/**
 * Information needed to bootstrap an Identity.
 *
 * Defined in: `../../../dxos/echo/metadata.proto`
 */
export interface IdentityRecord {
    identityKey: ReturnType<(typeof substitutions)["dxos.keys.PublicKey"]["decode"]>;
    deviceKey: ReturnType<(typeof substitutions)["dxos.keys.PublicKey"]["decode"]>;
    haloSpace: SpaceMetadata;
    /**
     * TODO(burdon): Not yet implemented. Must be null.
     *
     * Options:
     *   - proto3_optional = true
     */
    profileSpace?: SpaceMetadata;
    /**
     * Cached profile.
     *
     * Options:
     *   - proto3_optional = true
     */
    profile?: dxos_halo_credentials.ProfileDocument;
}
